import { readAtlasConfig } from "./config";

let enabled = false;
let initialized = false;

const isDebugEnabled = (env) => {
  const value = env ? env.REACT_APP_ATLAS_DEBUG : undefined;
  return typeof value === "string" && value.trim() === "true";
};

const logEvent = (e) => {
  try {
    console.log("[atlas] " + e.event_name, e);
  } catch (err) {}
};

// Registers the debug logger. Must run before sdk.init to see its events.
const registerDebugLogger = (sdk) => {
  if (typeof sdk.on !== "function") {
    return;
  }

  try {
    sdk.on("event", logEvent);
  } catch (e) {}
};

// Initializes the Atlas Analytics SDK once. Never throws; stays disabled
// when the SDK is not loaded, the settings are missing or init fails.
export function initAnalytics(env = process.env) {
  if (initialized) {
    return enabled;
  }
  initialized = true;

  try {
    const config = readAtlasConfig(env);
    const sdk = window.AnalyticsWeb;

    if (!config || !sdk || typeof sdk.init !== "function") {
      enabled = false;
      return enabled;
    }

    if (isDebugEnabled(env)) {
      registerDebugLogger(sdk);
    }

    sdk.init({
      ...config,
      platform: "OTT_WEB",
      initialConsent: "granted",
    });
    enabled = true;
  } catch (e) {
    enabled = false;
  }

  return enabled;
}

export function getAnalytics() {
  return enabled ? window.AnalyticsWeb : null;
}

export function isAnalyticsEnabled() {
  return enabled;
}

// Calls an SDK method when analytics is enabled. Never throws.
function callSdk(name, args) {
  if (!enabled) {
    return;
  }

  try {
    const sdk = window.AnalyticsWeb;

    if (!sdk || typeof sdk[name] !== "function") {
      return;
    }

    sdk[name](...args);
  } catch (e) {}
}

// credential: { type: "mw_token", token } as expected by the SDK.
export function setUser(credential, attributes) {
  // The app stores an HTTP Authorization value; the SDK accepts the raw token
  // only (its credential validator rejects whitespace, including "Bearer ").
  if (credential?.type === "mw_token" && typeof credential.token === "string") {
    credential = { ...credential, token: credential.token.trim().replace(/^Bearer\s+/i, "") };
  }
  callSdk("setUser", [credential, attributes]);
}

export function setProfile(profileId, isKids) {
  if (profileId === null || profileId === undefined || profileId === "") {
    return;
  }

  callSdk("setProfile", [String(profileId), { isKids: Boolean(isKids) }]);
}

// Re-reports the stored user and profile after a page reload, since the SDK
// does not keep the user token. Never throws; does nothing without both.
export function restoreIdentity(storage = window.localStorage) {
  if (!enabled) {
    return;
  }

  try {
    const token = storage.getItem("userToken");
    const profile = JSON.parse(storage.getItem("profile"));

    if (!token || !profile || profile.id === undefined || profile.id === null) {
      return;
    }

    setUser({ type: "mw_token", token });
    setProfile(profile.id, profile.kid);
  } catch (e) {}
}

export function reportScreenView(name, options) {
  callSdk("reportScreenView", [name, options]);
}

// The SDK bundle has no clearUser; setUser(null) clears the user instead.
export function clearUser() {
  callSdk("setUser", [null]);
}
